import {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Modal from 'components/Modal';
import { REQUIRED_ANGLE_PHOTOS } from 'constants/digitalLoanPhase2';
import {
  areRequiredPhotosComplete,
  replacePhotoSlot,
} from '../utils/phase2MockMapping';

const createPreview = (file) => (file ? URL.createObjectURL(file) : null);

const revokePreviews = (previews) => {
  if (!previews) return;
  if (previews.front) URL.revokeObjectURL(previews.front);
  (previews.angles || []).forEach((url) => {
    if (url) URL.revokeObjectURL(url);
  });
  if (previews.label) URL.revokeObjectURL(previews.label);
  if (previews.document) URL.revokeObjectURL(previews.document);
};

const TrashIcon = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 24 24'
    aria-hidden='true'
    focusable='false'
  >
    <path
      fill='currentColor'
      d='M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z'
    />
  </svg>
);

const PhotoCaptureScreen = ({
  literals,
  photos,
  onPhotosChange,
  onClearPhotos,
  onReview,
  onBack,
  requireDocument = false,
}) => {
  const fileInputRef = useRef(null);
  const [activeSlot, setActiveSlot] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [previews, setPreviews] = useState({
    front: null,
    angles: [],
    label: null,
    document: null,
  });

  useEffect(() => {
    const next = {
      front: createPreview(photos.front),
      angles: (photos.angles || []).map(createPreview),
      label: createPreview(photos.label),
      document: createPreview(photos.document),
    };
    setPreviews(next);
    return () => revokePreviews(next);
  }, [photos]);

  const canReview = areRequiredPhotosComplete(photos, requireDocument);
  const hasAnyPhoto = Boolean(
    photos.front
    || photos.label
    || photos.document
    || (photos.angles || []).some(Boolean),
  );

  const openSlot = (slot) => {
    setActiveSlot(slot);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !activeSlot) return;
    onPhotosChange(replacePhotoSlot(photos, activeSlot, file));
    setActiveSlot(null);
  };

  const handleConfirmClear = () => {
    setConfirmClear(false);
    onClearPhotos();
  };

  const renderSlot = (key, label, previewUrl) => (
    <div key={key} className='digital-loan-photo-capture__item'>
      <p className='digital-loan-photo-capture__caption'>{label}</p>
      <button
        type='button'
        className='digital-loan-photo-capture__slot'
        onClick={() => openSlot(key)}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={label}
            className='digital-loan-photo-capture__thumb'
          />
        ) : (
          <span>{literals.addPhoto}</span>
        )}
      </button>
      {previewUrl && (
        <button
          type='button'
          className='digital-loan-photo-capture__change'
          onClick={() => openSlot(key)}
        >
          {literals.changePhoto}
        </button>
      )}
    </div>
  );

  return (
    <div className='full-screen-container digital-loan-photo-capture'>
      <div className='screen-content'>
        <h1 className='digital-loan-phase2__title'>{literals.title}</h1>

        {renderSlot('front', literals.frontLabel, previews.front)}

        <p className='digital-loan-catalog__section'>{literals.anglesLabel}</p>
        <div className='digital-loan-photo-capture__angles'>
          {Array.from({ length: REQUIRED_ANGLE_PHOTOS }).map((_, index) => (
            renderSlot(
              `angle-${index}`,
              (literals.angleSlotLabels || [])[index],
              previews.angles[index],
            )
          ))}
        </div>

        {renderSlot('label', literals.labelLabel, previews.label)}
        <p className='digital-loan-photo-capture__hint'>{literals.labelHint}</p>

        {requireDocument && renderSlot(
          'document',
          literals.documentLabel,
          previews.document,
        )}
      </div>
      <div className='screen-actions'>
        <Button text={literals.review} disabled={!canReview} onClick={onReview} />
        <Button
          type='secondary'
          disabled={!hasAnyPhoto}
          onClick={() => setConfirmClear(true)}
        >
          <TrashIcon />
          <span>{literals.deleteAll}</span>
        </Button>
        <Button text={literals.back} type='secondary' onClick={onBack} />
      </div>
      <input
        ref={fileInputRef}
        type='file'
        className='d-none'
        accept='image/*'
        capture='environment'
        onChange={handleFileChange}
      />
      {confirmClear && (
        <Modal
          className='digital-loan-visit-modal'
          closeOnBackgroundClick={false}
          showCloseButton={false}
          onClose={() => setConfirmClear(false)}
        >
          <h2 className='digital-loan-visit-modal__title'>
            {literals.deleteConfirmTitle}
          </h2>
          <p className='digital-loan-phase2__body mb-4'>
            {literals.deleteConfirmBody}
          </p>
          <Button text={literals.confirmDelete} onClick={handleConfirmClear} />
          <Button
            text={literals.cancelDelete}
            type='secondary'
            onClick={() => setConfirmClear(false)}
          />
        </Modal>
      )}
    </div>
  );
};

PhotoCaptureScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  photos: PropTypes.object.isRequired,
  onPhotosChange: PropTypes.func.isRequired,
  onClearPhotos: PropTypes.func.isRequired,
  onReview: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  requireDocument: PropTypes.bool,
};

export default PhotoCaptureScreen;
